/**
 * InvitationActivationPage.js
 *
 * Public visitor page — accessible without authentication.
 * Route: /invitacion/:invitationGuid
 *
 * Implements the full 4-state visitor invitation activation flow:
 *   State 1   LANDING         — first-time activation, OTP enabled
 *   State 1b  REACTIVATION    — browser closed or multi-entry re-activation
 *   State 2   OTP_INPUT       — OTP verification form
 *   State 3   QR_DISPLAY      — Live dynamic QR (Layer 1 + Layer 4)
 *   Terminal  TERMINAL_USED / TERMINAL_EXPIRED / TERMINAL_CANCELLED
 *
 * Architecture ref: docs/VISITOR_ACCESS_CONTROL_ARCHITECTURE.md sections 16.2–16.5
 * Implementation ref: docs/AI_INTEGRATION_INSTRUCTIONS.md section 5.2
 *
 * SECURITY:
 *  - totpSecretSeed is stored ONLY in React component state (never in localStorage,
 *    sessionStorage, cookies, Redux or any persistent storage).
 *  - On unmount the seed is cleared automatically via useState lifecycle.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import tw from "twin.macro";
import QrLivePass from "components/misc/QrLivePass";
import LoadingSpinner from "components/misc/LoadingSpinner";
import { useVisitors } from "hooks/useVisitors";

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100dvh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 2rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Logo = styled.div`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #555;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #0f3460 0%, #533483 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 0.9rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #0f3460;
  border: 2px solid #0f3460;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background 0.2s;
  &:hover:not(:disabled) {
    background: rgba(15, 52, 96, 0.07);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const OtpInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: 0.4em;
  padding: 0.8rem;
  border: 2px solid #ccc;
  border-radius: 12px;
  outline: none;
  font-family: "Courier New", monospace;
  transition: border-color 0.2s;
  &:focus {
    border-color: #0f3460;
  }
  &.error {
    border-color: #e53e3e;
  }
  /* Remove spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
`;

const ErrorBanner = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  color: #c53030;
  font-size: 0.88rem;
  text-align: center;
`;

const InfoBanner = styled.div`
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  color: #2b6cb0;
  font-size: 0.88rem;
  text-align: center;
`;

const SuccessBanner = styled.div`
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  color: #276749;
  font-size: 0.88rem;
  text-align: center;
`;

const TerminalIcon = styled.div`
  font-size: 3.5rem;
  text-align: center;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  border-bottom: 1px solid #eee;
  font-size: 0.88rem;
  color: #444;
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #666;
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #0f3460;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  &:disabled {
    color: #999;
    cursor: default;
    text-decoration: none;
  }
`;

const CountdownText = styled.span`
  font-size: 0.82rem;
  color: #888;
`;

// ─── Page states ──────────────────────────────────────────────────────────────
const PAGE_STATE = {
  LOADING: "LOADING",
  LANDING: "LANDING",
  REACTIVATION: "REACTIVATION",
  OTP_INPUT: "OTP_INPUT",
  QR_DISPLAY: "QR_DISPLAY",
  TERMINAL_USED: "TERMINAL_USED",
  TERMINAL_EXPIRED: "TERMINAL_EXPIRED",
  TERMINAL_CANCELLED: "TERMINAL_CANCELLED",
  ERROR: "ERROR",
};

const MAX_OTP_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 60;

// ─── Main component ───────────────────────────────────────────────────────────

export default function InvitationActivationPage() {
  const { invitationGuid } = useParams();
  const { getInvitationDetails, requestOtp, activateInvitation, loading } = useVisitors();

  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [invitation, setInvitation] = useState(null);

  // Layer 1 + 4 — TOTP seed (MUST stay in component memory only, never persisted)
  const [totpSecretSeed, setTotpSecretSeed] = useState(null);
  const [validUntil, setValidUntil] = useState(null);

  // OTP form state
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [otpBlocked, setOtpBlocked] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [isActivating, setIsActivating] = useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const [generalError, setGeneralError] = useState("");

  // ── Clean up TOTP seed on unmount (security hygiene) ──────────────────────
  useEffect(() => {
    return () => {
      setTotpSecretSeed(null);
    };
  }, []);

  // ── Load invitation on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!invitationGuid) {
      setPageState(PAGE_STATE.ERROR);
      setGeneralError("Enlace de invitación inválido.");
      return;
    }
    loadInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationGuid]);

  const loadInvitation = async () => {
    setPageState(PAGE_STATE.LOADING);
    try {
      const data = await getInvitationDetails(invitationGuid);
      setInvitation(data);
      routeToState(data);
    } catch (err) {
      if (err.message === "INVITATION_NOT_FOUND") {
        setGeneralError("Esta invitación no existe o el enlace es inválido.");
      } else {
        setGeneralError("No se pudo cargar la invitación. Verifica tu conexión e intenta de nuevo.");
      }
      setPageState(PAGE_STATE.ERROR);
    }
  };

  /**
   * Routes to the correct page state based on invitationState from the API.
   * When OTP is disabled, auto-activates immediately (no OTP screen shown).
   */
  const routeToState = useCallback(
    async (data) => {
      const { invitationState, otpVerificationEnabled } = data;

      switch (invitationState) {
        case "NotActivated":
          if (!otpVerificationEnabled) {
            // OTP disabled — skip directly to activation
            await autoActivate();
          } else {
            setPageState(PAGE_STATE.LANDING);
          }
          break;
        case "NeedsReactivation":
        case "CanReactivate":
          setPageState(PAGE_STATE.REACTIVATION);
          break;
        case "Completed":
          setPageState(PAGE_STATE.TERMINAL_USED);
          break;
        case "Expired":
          setPageState(PAGE_STATE.TERMINAL_EXPIRED);
          break;
        case "Cancelled":
          setPageState(PAGE_STATE.TERMINAL_CANCELLED);
          break;
        default:
          setPageState(PAGE_STATE.LANDING);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invitationGuid]
  );

  /**
   * Auto-activates the invitation when OTP verification is disabled on the server.
   * Delivers TOTP seed and jumps directly to QR display.
   */
  const autoActivate = async () => {
    try {
      const result = await activateInvitation(invitationGuid, null);
      // Store seed in component state only — NEVER in localStorage
      setTotpSecretSeed(result.totpSecretSeed);
      setValidUntil(result.validUntil);
      setPageState(PAGE_STATE.QR_DISPLAY);
    } catch (err) {
      handleActivationError(err);
    }
  };

  // ── OTP request ────────────────────────────────────────────────────────────
  const handleRequestOtp = async () => {
    setOtpError("");
    setInfoMessage("");
    setIsRequestingOtp(true);
    try {
      await requestOtp(invitationGuid);
      setPageState(PAGE_STATE.OTP_INPUT);
      setInfoMessage("Código enviado a tu número registrado.");
      startResendCooldown();
    } catch (err) {
      if (err.message === "OTP_ALREADY_SENT") {
        setPageState(PAGE_STATE.OTP_INPUT);
        setInfoMessage("Ya se envió un código. Revisa tu WhatsApp o SMS.");
        startResendCooldown();
      } else if (err.message === "OTP_RATE_LIMITED") {
        setOtpError("Demasiados intentos. Espera 10 minutos antes de solicitar un nuevo código.");
      } else {
        setOtpError("No se pudo enviar el código. Inténtalo de nuevo.");
      }
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // ── OTP verification + activation ─────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      setOtpError("Por favor ingresa el código de 6 dígitos.");
      return;
    }
    setOtpError("");
    setIsActivating(true);
    try {
      const result = await activateInvitation(invitationGuid, otpValue);
      // Store seed in component state only — NEVER in localStorage
      setTotpSecretSeed(result.totpSecretSeed);
      setValidUntil(result.validUntil);
      setPageState(PAGE_STATE.QR_DISPLAY);
    } catch (err) {
      handleActivationError(err);
      const newAttempts = otpAttempts + 1;
      setOtpAttempts(newAttempts);
      if (newAttempts >= MAX_OTP_ATTEMPTS) {
        setOtpBlocked(true);
        setOtpError("Demasiados intentos fallidos. Solicita un nuevo código.");
      }
    } finally {
      setIsActivating(false);
    }
  };

  const handleActivationError = (err) => {
    const errorMap = {
      INVALID_OTP: "Código incorrecto o expirado. Inténtalo de nuevo.",
      ALREADY_ACTIVATED: "Esta invitación ya fue activada.",
      INVITATION_CANCELLED: "Esta invitación ha sido cancelada.",
      INVITATION_NOT_FOUND: "Invitación no encontrada.",
    };
    setOtpError(errorMap[err.message] || "Error al activar la invitación.");
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === "Enter" && !otpBlocked && !isActivating) {
      handleVerifyOtp();
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const InvitationDetails = () =>
    invitation ? (
      <>
        <DetailRow>
          <DetailLabel>Visitante:</DetailLabel>
          <span>{invitation.visitorName}</span>
        </DetailRow>
        {invitation.startDate && (
          <DetailRow>
            <DetailLabel>Válida desde:</DetailLabel>
            <span>{formatDate(invitation.startDate)}</span>
          </DetailRow>
        )}
        {invitation.endDate && (
          <DetailRow>
            <DetailLabel>Válida hasta:</DetailLabel>
            <span>{formatDate(invitation.endDate)}</span>
          </DetailRow>
        )}
        {invitation.remainingEntries != null && (
          <DetailRow>
            <DetailLabel>Accesos restantes:</DetailLabel>
            <span>{invitation.remainingEntries}</span>
          </DetailRow>
        )}
      </>
    ) : null;

  // ── Render states ──────────────────────────────────────────────────────────

  if (pageState === PAGE_STATE.QR_DISPLAY && totpSecretSeed) {
    return (
      <QrLivePass
        totpSecretSeed={totpSecretSeed}
        invitationGuid={invitationGuid}
        visitorName={invitation?.visitorName}
        validUntil={validUntil || invitation?.endDate}
      />
    );
  }

  if (pageState === PAGE_STATE.LOADING) {
    return (
      <PageContainer>
        <Card style={{ alignItems: "center" }}>
          <LoadingSpinner />
          <Subtitle>Cargando tu invitación…</Subtitle>
        </Card>
      </PageContainer>
    );
  }

  if (pageState === PAGE_STATE.ERROR) {
    return (
      <PageContainer>
        <Card>
          <TerminalIcon>⚠️</TerminalIcon>
          <Title>Enlace inválido</Title>
          <ErrorBanner>{generalError || "No se pudo cargar la invitación."}</ErrorBanner>
        </Card>
      </PageContainer>
    );
  }

  if (pageState === PAGE_STATE.TERMINAL_USED) {
    return (
      <PageContainer>
        <Card>
          <TerminalIcon>✅</TerminalIcon>
          <Title>Invitación utilizada</Title>
          <Subtitle>
            {invitation?.maxOpenDoorTimes > 0
              ? `Tu invitación ha sido utilizada completamente (${invitation.maxOpenDoorTimes} ${
                  invitation.maxOpenDoorTimes === 1 ? "acceso" : "accesos"
                }).`
              : "Esta invitación ya fue utilizada."}
          </Subtitle>
          <InfoBanner>
            Si necesitas una nueva invitación, contácta al propietario.
          </InfoBanner>
        </Card>
      </PageContainer>
    );
  }

  if (pageState === PAGE_STATE.TERMINAL_EXPIRED) {
    return (
      <PageContainer>
        <Card>
          <TerminalIcon>⏰</TerminalIcon>
          <Title>Invitación vencida</Title>
          <Subtitle>
            Esta invitación expiró el {formatDate(invitation?.endDate)}. Solicita una nueva al
            propietario si aún necesitas acceso.
          </Subtitle>
        </Card>
      </PageContainer>
    );
  }

  if (pageState === PAGE_STATE.TERMINAL_CANCELLED) {
    return (
      <PageContainer>
        <Card>
          <TerminalIcon>🚫</TerminalIcon>
          <Title>Invitación cancelada</Title>
          <Subtitle>El propietario canceló esta invitación. Contacta al propietario para más información.</Subtitle>
        </Card>
      </PageContainer>
    );
  }

  // ── State 2: OTP input ─────────────────────────────────────────────────────
  if (pageState === PAGE_STATE.OTP_INPUT) {
    return (
      <PageContainer>
        <Card>
          <Logo>🔒</Logo>
          <Title>Verificación de identidad</Title>
          <Subtitle>
            Ingresa el código de 6 dígitos enviado a tu número de WhatsApp o SMS registrado.
          </Subtitle>

          {infoMessage && <SuccessBanner>{infoMessage}</SuccessBanner>}
          {otpError && <ErrorBanner>{otpError}</ErrorBanner>}

          <OtpInput
            type="number"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={6}
            value={otpValue}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              setOtpValue(val);
              setOtpError("");
            }}
            onKeyDown={handleOtpKeyDown}
            disabled={otpBlocked || isActivating}
            className={otpError ? "error" : ""}
            aria-label="Código de verificación de 6 dígitos"
          />

          <PrimaryButton
            onClick={handleVerifyOtp}
            disabled={otpBlocked || isActivating || otpValue.length !== 6}
          >
            {isActivating ? "Verificando…" : "Verificar código"}
          </PrimaryButton>

          <div style={{ textAlign: "center" }}>
            {resendCooldown > 0 ? (
              <CountdownText>
                Reenviar código en {resendCooldown}s
              </CountdownText>
            ) : (
              <ResendLink
                onClick={handleRequestOtp}
                disabled={isRequestingOtp}
              >
                {isRequestingOtp ? "Enviando…" : "Reenviar código"}
              </ResendLink>
            )}
          </div>
        </Card>
      </PageContainer>
    );
  }

  // ── State 1b: Re-activation ────────────────────────────────────────────────
  if (pageState === PAGE_STATE.REACTIVATION) {
    const isMultiEntry = invitation?.invitationState === "CanReactivate";
    return (
      <PageContainer>
        <Card>
          <Logo>👋</Logo>
          <Title>
            {isMultiEntry
              ? `¿Listo para tu próximo acceso, ${invitation?.visitorName || "visitante"}?`
              : `¡Bienvenido de regreso, ${invitation?.visitorName || "visitante"}!`}
          </Title>
          <Subtitle>
            {isMultiEntry
              ? "Verifica tu identidad para obtener un nuevo código QR de acceso."
              : "Parece que cerraste la aplicación. Verifica tu identidad para recuperar tu código QR."}
          </Subtitle>

          <InvitationDetails />

          {otpError && <ErrorBanner>{otpError}</ErrorBanner>}

          <PrimaryButton onClick={handleRequestOtp} disabled={isRequestingOtp}>
            {isRequestingOtp ? "Enviando código…" : isMultiEntry ? "Obtener nuevo QR" : "Recuperar mi QR"}
          </PrimaryButton>
        </Card>
      </PageContainer>
    );
  }

  // ── State 1: Landing (first-time activation, OTP enabled) ─────────────────
  return (
    <PageContainer>
      <Card>
        <Logo>🎫</Logo>
        <Title>Tu invitación de acceso</Title>
        <Subtitle>
          Verifica tu identidad para obtener tu código QR de entrada al fraccionamiento.
        </Subtitle>

        <InvitationDetails />

        {otpError && <ErrorBanner>{otpError}</ErrorBanner>}
        {infoMessage && <InfoBanner>{infoMessage}</InfoBanner>}

        <PrimaryButton onClick={handleRequestOtp} disabled={isRequestingOtp || loading}>
          {isRequestingOtp ? "Enviando código…" : "Verificar mi identidad y obtener QR"}
        </PrimaryButton>

        <Subtitle style={{ fontSize: "0.8rem", color: "#888" }}>
          Se enviará un código de verificación al número de WhatsApp o SMS registrado por el
          propietario.
        </Subtitle>
      </Card>
    </PageContainer>
  );
}
